import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line, CartesianGrid } from 'recharts';
import Icon from 'components/AppIcon';
import { useAuth } from '../../../contexts/AuthContext';
import { supabase } from '../../../lib/supabase';

export default function AdminAnalyticsPage() {
  const navigate = useNavigate();
  const { user, isAuthenticated, isPlatformOwner, isSuperAdmin } = useAuth();

  const [stats, setStats] = useState({
    totalUsers: 0,
    totalCreators: 0,
    totalCreations: 0,
    totalVideos: 0,
    totalMusic: 0,
    totalGames: 0,
  });
  const [growthData, setGrowthData] = useState([]);
  const [creationsGrowth, setCreationsGrowth] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const isAdmin = isPlatformOwner || isSuperAdmin;

  const loadPlatformStats = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    setError(null);
    try {
      // Total users
      const { count: usersCount } = await supabase?.from('user_profiles')?.select('*', { count: 'exact', head: true });

      // Total creators
      const { count: creatorsCount } = await supabase?.from('creators')?.select('*', { count: 'exact', head: true });

      // All generated_assets
      const { data: assets, error: assetsErr } = await supabase?.from('generated_assets')?.select('id, asset_type, created_at')?.order('created_at', { ascending: false });

      if (assetsErr && assetsErr?.code !== 'PGRST116') throw assetsErr;

      // All generated_music
      const { data: music, error: musicErr } = await supabase?.from('generated_music')?.select('id, created_at')?.order('created_at', { ascending: false });

      if (musicErr && musicErr?.code !== 'PGRST116') throw musicErr;

      const assetList = assets || [];
      const musicList = music || [];

      const totalVideos = assetList?.filter((a) => a?.asset_type === 'video')?.length;
      const totalGames = assetList?.filter((a) => a?.asset_type === 'game')?.length;

      setStats({
        totalUsers: usersCount || 0,
        totalCreators: creatorsCount || 0,
        totalCreations: assetList?.length + musicList?.length,
        totalVideos,
        totalMusic: musicList?.length,
        totalGames,
      });

      // Build last 14 days growth
      const days = buildLastNDays(14);
      const userGrowthMap = {};
      const creationsMap = {};
      days?.forEach((d) => { userGrowthMap[d] = 0; creationsMap[d] = 0; });

      // User signups per day (from user_profiles)
      const { data: profiles } = await supabase?.from('user_profiles')?.select('created_at')?.gte('created_at', new Date(Date.now() - 14 * 86400000)?.toISOString());

      (profiles || [])?.forEach((p) => {
        const day = new Date(p.created_at)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        if (userGrowthMap?.[day] !== undefined) userGrowthMap[day]++;
      });

      assetList?.forEach((a) => {
        const day = new Date(a.created_at)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        if (creationsMap?.[day] !== undefined) creationsMap[day]++;
      });
      musicList?.forEach((m) => {
        const day = new Date(m.created_at)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        if (creationsMap?.[day] !== undefined) creationsMap[day]++;
      });

      setGrowthData(days?.map((d) => ({ day: d, users: userGrowthMap?.[d] })));
      setCreationsGrowth(days?.map((d) => ({ day: d, creations: creationsMap?.[d] })));
    } catch (err) {
      console.error('[AdminAnalytics]', err?.message);
      setError(err?.message || 'Failed to load platform analytics');
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    if (!isAuthenticated) { navigate('/home-landing-screen'); return; }
    if (!isAdmin) { navigate('/creator-dashboard'); return; }
    loadPlatformStats();
  }, [isAuthenticated, isAdmin, loadPlatformStats, navigate]);

  const STAT_CARDS = [
    { label: 'Total Users', value: stats?.totalUsers, icon: 'Users', color: '#6366F1' },
    { label: 'Total Creators', value: stats?.totalCreators, icon: 'Star', color: '#8B5CF6' },
    { label: 'Total Creations', value: stats?.totalCreations, icon: 'Layers', color: '#F59E0B' },
    { label: 'Videos Generated', value: stats?.totalVideos, icon: 'Video', color: '#EC4899' },
    { label: 'Music Generated', value: stats?.totalMusic, icon: 'Music', color: '#10B981' },
    { label: 'Games Generated', value: stats?.totalGames, icon: 'Gamepad2', color: '#06B6D4' },
  ];

  const tooltipStyle = { background: 'var(--color-card)', border: '1px solid var(--color-border)', borderRadius: 8, color: 'var(--color-foreground)', fontSize: 12 };

  return (
    <div className="min-h-screen" style={{ background: 'var(--color-background)' }}>
      {/* Header */}
      <div className="sticky top-0 z-40 border-b" style={{ background: 'var(--color-card)', borderColor: 'var(--color-border)' }}>
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center gap-3">
          <button onClick={() => navigate('/admin/creators')} className="p-2 rounded-lg hover:bg-[var(--color-muted)] transition-colors">
            <Icon name="ArrowLeft" size={18} color="var(--color-foreground)" />
          </button>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #EF4444, #F59E0B)' }}>
              <Icon name="BarChart2" size={16} color="white" />
            </div>
            <div>
              <h1 className="text-base font-bold" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>Platform Analytics</h1>
              <p className="text-xs" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>Admin — Platform-wide statistics</p>
            </div>
          </div>
          <button onClick={loadPlatformStats} className="ml-auto p-2 rounded-lg hover:bg-[var(--color-muted)] transition-colors" title="Refresh">
            <Icon name="RefreshCw" size={16} color="var(--color-muted-foreground)" />
          </button>
        </div>
      </div>
      <div className="max-w-6xl mx-auto px-4 py-6 flex flex-col gap-6">
        {!isAdmin && (
          <div className="p-4 rounded-xl text-sm" style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', color: '#EF4444' }}>
            Admin access required.
          </div>
        )}
        {error && (
          <div className="p-4 rounded-xl text-sm" style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', color: '#EF4444' }}>{error}</div>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {STAT_CARDS?.map((s) => (
            <div key={s?.label} className="rounded-2xl p-4 flex flex-col items-center gap-2 text-center" style={{ background: 'var(--color-card)', border: '1px solid var(--color-border)' }}>
              {loading ? (
                <div className="w-full h-14 rounded-lg animate-pulse" style={{ background: 'var(--color-muted)' }} />
              ) : (
                <>
                  <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: `${s?.color}18` }}>
                    <Icon name={s?.icon} size={16} color={s?.color} />
                  </div>
                  <p className="text-2xl font-bold" style={{ color: s?.color, fontFamily: 'var(--font-data)' }}>{s?.value?.toLocaleString()}</p>
                  <p className="text-xs" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>{s?.label}</p>
                </>
              )}
            </div>
          ))}
        </div>

        {/* Growth Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* User Growth */}
          <div className="rounded-2xl p-5" style={{ background: 'var(--color-card)', border: '1px solid var(--color-border)' }}>
            <h3 className="text-sm font-bold mb-4" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>New Users (Last 14 Days)</h3>
            {loading ? (
              <div className="h-48 rounded-xl animate-pulse" style={{ background: 'var(--color-muted)' }} />
            ) : (
              <ResponsiveContainer width="100%" height={180}>
                <LineChart data={growthData} margin={{ top: 4, right: 4, bottom: 0, left: -20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                  <XAxis dataKey="day" tick={{ fontSize: 9, fill: 'var(--color-muted-foreground)' }} interval={1} />
                  <YAxis tick={{ fontSize: 10, fill: 'var(--color-muted-foreground)' }} allowDecimals={false} />
                  <Tooltip contentStyle={tooltipStyle} />
                  <Line type="monotone" dataKey="users" stroke="#6366F1" strokeWidth={2} dot={{ r: 3, fill: '#6366F1' }} name="New Users" />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>

          {/* Creations Growth */}
          <div className="rounded-2xl p-5" style={{ background: 'var(--color-card)', border: '1px solid var(--color-border)' }}>
            <h3 className="text-sm font-bold mb-4" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>Creations per Day (Last 14 Days)</h3>
            {loading ? (
              <div className="h-48 rounded-xl animate-pulse" style={{ background: 'var(--color-muted)' }} />
            ) : (
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={creationsGrowth} margin={{ top: 4, right: 4, bottom: 0, left: -20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                  <XAxis dataKey="day" tick={{ fontSize: 9, fill: 'var(--color-muted-foreground)' }} interval={1} />
                  <YAxis tick={{ fontSize: 10, fill: 'var(--color-muted-foreground)' }} allowDecimals={false} />
                  <Tooltip contentStyle={tooltipStyle} />
                  <Bar dataKey="creations" fill="#F59E0B" radius={[4, 4, 0, 0]} name="Creations" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* Quick Links */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <button
            onClick={() => navigate('/admin/creators')}
            className="flex items-center gap-3 p-4 rounded-2xl text-left transition-all hover:opacity-80"
            style={{ background: 'var(--color-card)', border: '1px solid var(--color-border)' }}
          >
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'rgba(99,102,241,0.15)' }}>
              <Icon name="Users" size={18} color="#6366F1" />
            </div>
            <div>
              <p className="text-sm font-bold" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>Manage Creators</p>
              <p className="text-xs" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>View, disable, or remove creator accounts</p>
            </div>
            <Icon name="ChevronRight" size={16} color="var(--color-muted-foreground)" className="ml-auto" />
          </button>
          <button
            onClick={() => navigate('/community-gallery')}
            className="flex items-center gap-3 p-4 rounded-2xl text-left transition-all hover:opacity-80"
            style={{ background: 'var(--color-card)', border: '1px solid var(--color-border)' }}
          >
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'rgba(245,158,11,0.15)' }}>
              <Icon name="Image" size={18} color="#F59E0B" />
            </div>
            <div>
              <p className="text-sm font-bold" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>Community Gallery</p>
              <p className="text-xs" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>Browse all published creations</p>
            </div>
            <Icon name="ChevronRight" size={16} color="var(--color-muted-foreground)" className="ml-auto" />
          </button>
        </div>
      </div>
    </div>
  );
}

function buildLastNDays(n) {
  const days = [];
  for (let i = n - 1; i >= 0; i--) {
    const d = new Date();
    d?.setDate(d?.getDate() - i);
    days?.push(d?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }));
  }
  return days;
}
